import Breadcrumbs from 'components/Breadcrumbs'
import ExpenseForm from 'components/ExpenseForm'
import LoadingIcon from 'components/LoadingIcon'
import { createExpense, fetchExpense, updateExpense } from 'config/api'
import routes from 'config/routes'
import { useBankrolls } from 'hooks'
import PropTypes from 'prop-types'
import React from 'react'
import { Col, Row } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useToasts } from 'react-toast-notifications'
import { parseError, sleep } from 'utils'
import BankrollFilter from 'components/BankrollFilter'

function Expense({
  location: { pathname, state: routerState },
  history,
  match,
}) {
  const { addToast } = useToasts()
  const { currentBankroll, bankrolls, selectBankroll, createBankroll } =
    useBankrolls()
  const [editing, setEditing] = React.useState(false)
  const [expense, setExpense] = React.useState()
  const qClient = useQueryClient()

  const qExpense = useQuery(
    ['expenses', match.params.id],
    async () => {
      await sleep(500)
      return fetchExpense(currentBankroll.id, match.params.id)
    },
    {
      onSuccess: data => {
        setExpense(data)
      },
      onError: err => {
        console.error('Error retrieving expense')
        console.error(err)
        history.push(routes.expenses)
      },
      enabled: pathname !== routes.expenseNew,
    },
  )

  const mExpense = useMutation(
    ({ action, id, ...expense }) => {
      switch (action) {
        case 'create':
          return createExpense(currentBankroll.id, expense)
        case 'update':
          return updateExpense(currentBankroll.id, id, expense)
        default:
          throw new Error('unknown action')
      }
    },
    {
      onSuccess: () => {
        qClient.invalidateQueries('bankrolls')
        qClient.invalidateQueries('expenses')
      },
    },
  )

  React.useEffect(() => {
    setEditing(pathname !== routes.expenseNew)
    if (routerState && routerState.expense) {
      setExpense(routerState.expense)
    }
  }, [history, match, pathname, routerState])

  const submit = async expense => {
    try {
      await mExpense.mutateAsync({
        ...expense,
        action: editing ? 'update' : 'create',
      })
      addToast(`${editing ? 'Updated' : 'Created'} expense`, {
        appearance: 'success',
        autoDismiss: true,
      })
      history.push(routes.expenses)
    } catch (err) {
      const { non_field_errors } = parseError(err)
      addToast(
        non_field_errors || `Error ${editing ? 'editing' : 'adding'} expense`,
        {
          appearance: 'error',
          autoDismiss: true,
        },
      )
      throw err
    }
  }

  const initial = expense
    ? {
        ...expense,
      }
    : null

  return (
    <div id="expense">
      <Breadcrumbs
        crumbs={[
          ['DASHBOARD', routes.dashboard],
          ['EXPENSES', routes.expenses],
          [editing ? 'EDIT' : 'ADD', ''],
        ]}
      />
      <Row>
        <Col className="d-flex flex-wrap justify-content-start justify-content-md-between">
          <BankrollFilter
            current={currentBankroll}
            choices={bankrolls}
            onChange={selectBankroll}
            onCreate={createBankroll}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mt-3">
          {qExpense.isLoading ? (
            <LoadingIcon />
          ) : (
            <ExpenseForm
              onSubmit={submit}
              editing={editing}
              initial={initial}
              customFields={currentBankroll.custom_expense_fields}
              showPlayerField={currentBankroll.shared_users.length > 0}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}

Expense.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
}

export default Expense
